import { Component, AfterViewInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, inject, Input, ViewChild, ElementRef } from '@angular/core';
import {
  IObcNavigationInstruments
} from '../../shared/interfaces/oicl-obenbridge-interfaces/obc-navigations-instrument.interface';
import { DefaultObcNavInstruments, IDefaultObcNavInstruments } from '../../shared/oicl-navigations-components/oicl-navigations-instrument.constant';
import '@oicl/openbridge-webcomponents/dist/navigation-instruments/instrument-field/instrument-field.js';
import { InstrumentFieldService } from '../services/instrument-field.service';
import { filter, Subject, takeUntil } from 'rxjs';
import { INgsiAIS } from '../../shared/interfaces/ngsi/ais-interface';
import { INotification, NgsildConnectorService } from 'ngsild-connector';
import { INgsiEntityIdAttrs } from '../../shared/interfaces/ngsi/entity.interface';
import * as ModuleEntityTypesConfig from '../../shared/config/module.ngsi-entity-types.constants';
import { IInstrumentFieldWidgetPermissions } from '../../shared/interfaces/widget-permissions/widgetPermission.interface';
import { IOiclOpenBridgeInstrumentFieldWidget } from '../../shared/interfaces/instrument-field/oicl-openbridge-instrument-field-widget.interface';
import {
  IAppDashboard,
  INgsiSubscriptionWidget,
  IPermissionsFlags,
  IPluginPermissions,
  IUser,
  ModelService,
  PluginInjectorWidget,
  PluginInjectorService,
} from 'core-client/api';




@Component({
  selector: 'lib-instrument-field-view',
  imports: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './instrument-field-view.component.html',
  styleUrl: './instrument-field-view.component.scss',
})
export class InstrumentFieldViewComponent implements AfterViewInit, OnDestroy {
  // Services
  protected readonly InstrumentFieldService: InstrumentFieldService = inject(InstrumentFieldService);
  private readonly pluginInjectorService: PluginInjectorService = inject(PluginInjectorService);
  private readonly ngsildConnectorService: NgsildConnectorService = inject(NgsildConnectorService);
  private readonly modelService: ModelService = inject(ModelService);

  // Variables
  private resizeObserver: ResizeObserver | undefined;
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();
  protected widgetId: string | undefined;
  protected currDashboardId: string | undefined;
  private instrumentKey: keyof IDefaultObcNavInstruments | undefined;

  private widgetSubscriptions: INgsiSubscriptionWidget[] = [];
  protected entityType: string = ModuleEntityTypesConfig.NGSI_ENTITY_TYPES.instrumentField.entityTypes[0].AIS;
  protected ngsiEntityId: Omit<INgsiEntityIdAttrs, 'ngsildPrefix'> | undefined;
  protected openBridgeInstrumentFieldWidget: IOiclOpenBridgeInstrumentFieldWidget | undefined;

  protected widgetPermissions: IInstrumentFieldWidgetPermissions = {
    widgetEnabled: false,
    widgetSettingsEditEnabled: false,
    ngsildConnectorEnabled: false,
  };
  protected extraPermissionRefs: { ngsildConnectorEnabled: string } = {
    ngsildConnectorEnabled: 'authorizeNgsildConnector'
  };
  protected obcInstrumentFieldInstance: Required<Pick<IObcNavigationInstruments, 'instrumentField'>> = {
    instrumentField: DefaultObcNavInstruments.hdg
  }

  @ViewChild('obcNavigationInstrumentContainer', { static: false })
  protected obcNavigationInstrumentContainer!: ElementRef;

  @Input()
  data: PluginInjectorWidget.IWidget | undefined;

  @Input()
  isHeaderHovered: boolean = false;

  async ngAfterViewInit(): Promise<void> {
    if (!this.data?.appPluginWidget) {
      console.error(`Widget data not correctly loaded.`);
      return;
    }
    // Observe changes such as onHover for widget headers
    // this.observeStyleChanges();

    this.widgetSubscriptions = await this.modelService._ngsiSubscriptionWidget.listAll(`type=${this.entityType}`);

    await this.getWidgetAccessRights(
      this.modelService._user.getUser(),
      this.pluginInjectorService,
      this.data
    );

    const user = this.modelService._user.getUser();
    if (!user?._id) {
      console.error(`[Instrument Field Widget] User not found.`);
      return;
    }

    this.currDashboardId = user.active_dashboard;

    this.openBridgeInstrumentFieldWidget = this.data.appPluginWidget;
    this.widgetId = this.openBridgeInstrumentFieldWidget?._id;
    this.ngsiEntityId = this.openBridgeInstrumentFieldWidget?.settings.entityId;
    this.instrumentKey = this.openBridgeInstrumentFieldWidget?.settings.instrumentKey;

    if (this.instrumentKey === undefined) {
      this.instrumentKey = "hdg";
    }

    this.obcInstrumentFieldInstance.instrumentField = { ...DefaultObcNavInstruments[this.instrumentKey] }
    this.listenOnWidgetSettingsChanges(this.widgetId);
    this.listenOnNgsildServiceEvents();
  }

  private observeStyleChanges(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.setWidgetHeaderOnHover(this.isHeaderHovered);
    });

    if (this.obcNavigationInstrumentContainer?.nativeElement) {
      this.resizeObserver.observe(this.obcNavigationInstrumentContainer?.nativeElement)
    }
  }

  private setWidgetHeaderOnHover(isHeaderHovered: boolean): void {
    if (this.obcNavigationInstrumentContainer?.nativeElement) {
      this.obcNavigationInstrumentContainer.nativeElement.style.marginTop = isHeaderHovered ? '35px' : '0';
    }
  }

  async ngOnDestroy(): Promise<void> {
    // Clear to all component subscriptions
    this.ngUnsubscribe$.complete();

    // Clear extern plugins subscriptions
    if (this.widgetId) {
      this.ngsildConnectorService.unregisterNotificationHandler(this.widgetId, <string>this.openBridgeInstrumentFieldWidget?.class);
      this.InstrumentFieldService.removeInstrumentFieldEntity(this.widgetId);
      this.InstrumentFieldService.removeInstrumentFieldId(this.widgetId);
    }
  }

  private listenOnNgsildServiceEvents(): void {
    // Register when the component is called up
    this.registerForNotification(this.widgetId, this.ngsiEntityId);

    // Listen if the plugin is switch from deactivate to activate and widget is on dashboard visible
    this.ngsildConnectorService.socket.on('connect', () => {
      if (this.ngsildConnectorService.socket.ioSocket.connected && this.widgetPermissions.ngsildConnectorEnabled) {
        this.registerForNotification(this.widgetId, this.ngsiEntityId);
      }
    });
  }

  private registerForNotification(widgetId: string | undefined, ngsiEntityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined): void {
    if (!widgetId) {
      console.info('Instrument Field WidgetId is not assigned  - registration is aborted');
      return;
    }

    if (!ngsiEntityId || !this.currDashboardId) {
      return;
    }

    this.ngsildConnectorService.registerNotificationHandler(widgetId, this.currDashboardId,<string>this.openBridgeInstrumentFieldWidget?.class, (notification: INotification) => {
      const isRelevant = this.filterIncomingNgsINotification(
        notification,
        widgetId,
        ngsiEntityId,
        this.widgetSubscriptions
      )

      if (isRelevant) {
        const entity = notification.data[0] as INgsiAIS;
        this.updateInstrumentFieldInfos(entity);
      }
    });
  }

  private filterIncomingNgsINotification(
    notification: INotification,
    widgetId: string | undefined,
    entityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined,
    widgetSubscriptions: INgsiSubscriptionWidget[]
  ): boolean {
    const entity = notification.data[0] as INgsiAIS;
    const widgetEntityId = this.getWidgetEntityId(entityId);

    const subForWidgetExist = widgetSubscriptions.some((widgetSubscription: INgsiSubscriptionWidget) =>
      widgetSubscription.subscriptionId === notification.subscriptionId &&
      widgetSubscription.app_plugin_widget === widgetId
    );

    const sameEntityId = entity.id === widgetEntityId;
    return (subForWidgetExist && sameEntityId);
  }

  private getWidgetEntityId(entityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined): string | undefined {
    if (!entityId?.idPattern) {
      return undefined;
    }

    return entityId?.owner ?
      `${ModuleEntityTypesConfig.NGSI_ENTITY_ID_PREFIX}${entityId?.type}:${entityId?.owner}:${entityId?.idPattern}` :
      `${ModuleEntityTypesConfig.NGSI_ENTITY_ID_PREFIX}${entityId?.type}:${entityId?.idPattern}`;
  }

  private listenOnWidgetSettingsChanges(widgetId: string): void {
    this.InstrumentFieldService.getInstrumentFieldId$(widgetId)
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter(
          (instrumentKey: keyof IDefaultObcNavInstruments | undefined): instrumentKey is keyof IDefaultObcNavInstruments => instrumentKey != undefined)
      )
      .subscribe(async (instrumentKey: keyof IDefaultObcNavInstruments) => {
        if (!this.widgetId) {
          console.error(`Widget data not correctly loaded.`);
          return;
        }

        this.instrumentKey = instrumentKey;
        this.obcInstrumentFieldInstance.instrumentField = { ...DefaultObcNavInstruments[instrumentKey] };

        if (this.ngsiEntityId) {
          this.widgetSubscriptions = await this.modelService._ngsiSubscriptionWidget.listAll(`type=${this.entityType}`);
          this.listenOnNgsildServiceEvents();
        }
      })

    this.InstrumentFieldService.getInstrumentFieldEntity$(widgetId)
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((entityId: Omit<INgsiEntityIdAttrs, 'ngsildPrefix'> | undefined): entityId is Omit<INgsiEntityIdAttrs, 'ngsildPrefix'> =>
          entityId != undefined &&
          entityId.idPattern != this.ngsiEntityId?.idPattern
        ),
      ).subscribe(async (entityId: Omit<INgsiEntityIdAttrs, 'ngsildPrefix'>) => {
        if (!this.widgetId) {
          console.error(`Widget data not correctly loaded.`);
          return;
        }

        this.ngsiEntityId = entityId;

        if (this.instrumentKey != undefined) {
          this.widgetSubscriptions = await this.modelService._ngsiSubscriptionWidget.listAll(`type=${this.entityType}`);

          this.listenOnNgsildServiceEvents();
        }
      })
  }

  private async getWidgetAccessRights(
    user: IUser,
    pluginInjectorService: PluginInjectorService,
    widget: PluginInjectorWidget.IWidget
  ): Promise<void> {
    const appDashboard = user.app_dashboards.find(
      (dashboard: IAppDashboard) => dashboard._id === user.active_dashboard
    );

    if (appDashboard) {
      const pluginPermissions = await pluginInjectorService.permissionService.getPermissions(
        appDashboard,
        user
      );

      const accessRights = pluginPermissions.find(
        (plugin_permission: IPluginPermissions) => plugin_permission.plugin_id === widget?.pluginId
      );

      if (accessRights?.widget_permissions) {
        const modulePermissions = accessRights.widget_permissions[widget.pluginModule.toLowerCase()] as IPermissionsFlags;

        const widgetEnabled = widget?.widgetPermissions?.widgetEnabledPermission?.permissionRef;
        const widgetSettingsEnabled =
          widget?.widgetPermissions?.widgetBasePermissions?.widgetSettingsEnabledPermission?.permissionRef;
        const ngsildConnectorEnabled =
          widget?.widgetPermissions?.widgetAdditionalPermissions?.[this.extraPermissionRefs.ngsildConnectorEnabled]?.permissionRef;

        this.widgetPermissions = {
          widgetEnabled: widgetEnabled ? modulePermissions[widgetEnabled] : false,
          widgetSettingsEditEnabled: widgetSettingsEnabled ? modulePermissions[widgetSettingsEnabled] : false,
          ngsildConnectorEnabled: ngsildConnectorEnabled ? modulePermissions[ngsildConnectorEnabled] : false
        };
      }
    }
  }

  private updateInstrumentFieldInfos(entity: INgsiAIS): void {
    let value: number | undefined = undefined;

    value = entity?.[this.obcInstrumentFieldInstance.instrumentField.aisKey];
    if (value != undefined) {
      this.obcInstrumentFieldInstance.instrumentField.value = value;
    }
  }
}
